import { useState, useEffect, useRef } from 'react';
import { useToast } from '../../context/ToastProvider.js';
import config from '../../constants/config.js';
import './../../styles/Modal.css';

function FileEditorModal({
    onClose,
    onSelectedFile
}) {
    const addToast = useToast();
    const [ uploads, setUploads ] = useState( {} );
    const [ uploading, setUploading ] = useState( [] );

    const [ selectedFileIndex, setSelectedFileIndex ] = useState( null );
    const [ loadingView , setLoadingView ] = useState( false );
    const alreadyLoadedOnce = useRef( false );

    useEffect( () => {
        async function load() {
            setLoadingView( true );

            /**
             * Load Image Files. Not currently paginated. Pulls down all uploaded file metadata
            */
            const _files = await getImageFiles();
            if ( _files != null ) {
                setUploads( _files );
            }

            setLoadingView( false );
            alreadyLoadedOnce.current = true;
        }

        load();
    } , [] );

    async function uploadFiles( files ) {
        const filteredFiles = files.filter( file => {
            return !uploading.some( u => u.name === file.name );
        });

        if ( filteredFiles.length === 0 ) {
            addToast( "This file is already uploading.", true, 4000 );
            return;
        }

        addUploadingFiles( filteredFiles );

        try {
            // convert files array into binary and send to endpoint
            const filesAsBlobs = [];
            for ( let i = 0; i < filteredFiles?.length; i++ ) {
                const file = filteredFiles[i];
                const buffer = await file.arrayBuffer();
                filesAsBlobs.push({
                    name: file.name,
                    type: file.type,
                    data: Array.from( new Uint8Array( buffer ) )
                });
            }

            const payload = {
                files: filesAsBlobs
            };

            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/images`;
            const requestSettings = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify( payload )
            };


            const response = await fetch( url , requestSettings );
            const data = await response.json();
            if ( data?.errors?.length > 0 ) {
                addToast( `Failed to upload new images - ${data?.errors[0].message}`, true , 5000 );
            } else {
                addToast( `Added New Images`, false , 5000 );
                const images = data?.imagesUploaded || [];
                if ( images.length > 0 ) {
                    setUploads( prevUploads => ({
                        ...prevUploads,
                        images: [
                            ...images,
                            ...( prevUploads.images || [] )
                        ]
                    }));
                }
            }
        } catch ( error ) {
            console.log( error );
            addToast( `Failed to add new images - Could not reach server`, true , 5000 );
        } finally {
            removeUploadingFiles( filteredFiles );
        }

        return;
    }

    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    function removeUploadingFiles( files = [] ) {
        setUploading( prevUploading => {
            const updated = prevUploading.filter(upload => {
                return !files.some(file => file.name === upload.name);
            });

            return updated;
        });
    }

    function addUploadingFiles( files = [] ) {
        const newUploadingFiles = files
            .filter(file => file.type.startsWith("image/") && file.type !== "image/gif")
            .map(file => ({
                name: file.name,
                type: file.type.split('/')[1].toUpperCase(),
            }));

        setUploading(prev => [...prev, ...newUploadingFiles]);

        return newUploadingFiles;
    }

    async function getImageFiles( ) {
        let images = null;

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/images?page=1&limit=1000`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data != null && data?.images?.length > 0 ) {
                images = data;
            }

        } catch ( error ) {
            console.error( error );
        }
        
        return images;
    }

    return(
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content extra-wide" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>
                    <span>×</span>
                    <span className="sr-only">Close modal</span>
                </button>

                <h1>Select Image</h1>
                
                <div className="modal-body-grid">
                    {!loadingView &&
                        <div
                            className="file-dropzone"
                            onDragOver={ ( e ) => e.preventDefault() }
                            onDrop={ ( e ) => {
                                e.preventDefault();
                                const files = Array.from( e.dataTransfer.files ).filter(file =>
                                    file.type.startsWith("image/") && file.type !== "image/gif"
                                );
                                uploadFiles( files );
                            }}
                        >
                            <input
                                type="file"
                                accept="image/png, image/jpeg, image/jpg, image/webp, image/svg+xml, image/bmp"
                                multiple
                                onChange={ (e) => {
                                    const files = Array.from(e.target.files);
                                    uploadFiles( files );
                                }}
                            />
                            <p>Drag and drop images here, or click to select</p>
                        </div>
                    }

                    {loadingView &&
                        <div className="file-dropzone loading">
                            <div className="pulse-loader" />
                            <p>Loading uploads…</p>
                        </div>
                    }

                    <div className="uploads-grid">
                        {loadingView &&
                            Array.from({ length: 7 }).map((_, index) => (
                                <div className="upload-item loading" key={`loading-${index}`}>
                                    <div className="upload-placeholder pulse" />
                                    <div className="upload-info">
                                        <div className="file-name loading-bar" />
                                        <div className="file-type loading-bar short" />
                                    </div>
                                </div>
                            ))
                        }

                        {!loadingView && uploading.length === 0 && uploads?.images == null && (
                            <div className="upload-empty-message">
                                You don't have any images yet. Upload one to get started.
                            </div>
                        )}

                        {!loadingView && uploading.map(( data, index, srcArray ) => {
                            const file = srcArray[ srcArray.length - 1 - index ];
                            return(
                                <div className="upload-item uploading" key={`uploading-${index}`}>
                                    <div className="upload-placeholder">Uploading…</div>
                                    <div className="upload-info">
                                        <div className="file-name">{file.name}</div>
                                        <div className="file-type">{file.type}</div>
                                    </div>
                                </div>
                            )
                        })}

                        {!loadingView && uploads?.images?.map((file, index) => (
                            <div 
                                className={`upload-item selectable ${selectedFileIndex === index ? 'selected' : ''}`}
                                onClick={() => setSelectedFileIndex(index)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') setSelectedFileIndex(index);
                                }}
                                aria-pressed={selectedFileIndex === index}
                                tabIndex={0} 
                                key={file.id}
                            >
                                <img src={file.preview_url} alt={file.name} />
                                <div className="upload-info">
                                    <div className="file-name">{file.name}</div>
                                    <div className="file-type">{file.type}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="modal-controls">
                    <button type="button" className="cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="done-btn"
                        disabled={ selectedFileIndex === null }
                        onClick={() => {
                            if ( selectedFileIndex !== null ) {
                                onSelectedFile( uploads?.images[ selectedFileIndex ] );
                            }
                        }}
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    )
}

export default FileEditorModal;